import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Switch } from '../components/ui/switch';
import { toast } from 'sonner';
import { ArrowLeft, Plus, Loader2, GraduationCap } from 'lucide-react';

export default function PostCoursePage() {
  const navigate = useNavigate();
  const { api } = useAuth();

  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    institution_name: '',
    category: '',
    subcategory: '',
    level: 'beginner',
    instructor: '',
    duration: '',
    price: '',
    original_price: '',
    currency: 'GBP',
    delivery_mode: 'online',
    location: '',
    start_date: '',
    end_date: '',
    certification: '',
    requirements: '',
    outcomes: '',
    image_url: '',
    video_url: '',
    is_active: true
  });

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value
    });
  };

  const FALLBACK_CATEGORIES = ['Technology', 'Business', 'Design', 'Marketing', 'Languages', 'Other']
    .map(name => ({ id: null, name, slug: name.toLowerCase() }));

  const [courseCategories, setCourseCategories] = useState(FALLBACK_CATEGORIES);
  const [courseSubcategories, setCourseSubcategories] = useState([]);
  const [currencies, setCurrencies] = useState([]);

  useEffect(() => {
    api.get('/courses/categories')
      .then(res => {
        const cats = res.data.categories || [];
        if (cats.length > 0) {
          setCourseCategories(cats.map(c => typeof c === 'string'
            ? { id: null, name: c, slug: c.toLowerCase() }
            : { id: c.id, name: c.name || c.slug, slug: c.slug || c.name }));
        }
      })
      .catch(() => {});
    api.get('/currencies')
      .then(res => setCurrencies(res.data.currencies || []))
      .catch(() => setCurrencies([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    setFormData(prev => ({ ...prev, subcategory: '' }));
    const cat = courseCategories.find(c => c.slug === formData.category);
    if (!cat || !cat.id) { setCourseSubcategories([]); return; }
    api.get(`/courses/subcategories?category_id=${cat.id}`)
      .then(res => setCourseSubcategories(res.data.subcategories || []))
      .catch(() => setCourseSubcategories([]));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formData.category, courseCategories]);

  /** FastAPI validation errors (422) come back as an array of {msg, loc} objects,
   * not a string — passing that straight into toast.error() throws "Objects are
   * not valid as a React child" and (with no error boundary) blanks the page. */
  const getErrorMessage = (error, fallback) => {
    const detail = error.response?.data?.detail;
    if (!detail) return fallback;
    if (typeof detail === 'string') return detail;
    if (Array.isArray(detail)) {
      return detail.map(d => d.msg || JSON.stringify(d)).join('; ');
    }
    return fallback;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (!formData.title.trim()) {
      toast.error('Course title is required');
      return;
    }
    if (!formData.description.trim()) {
      toast.error('Course description is required');
      return;
    }
    if (!formData.institution_name.trim()) {
      toast.error('Institution / provider name is required');
      return;
    }
    if (!formData.category.trim()) {
      toast.error('Course category is required');
      return;
    }
    if (!formData.duration.trim()) {
      toast.error('Course duration is required');
      return;
    }
    if (!formData.price) {
      toast.error('Course price is required');
      return;
    }

    setLoading(true);
    try {
      const payload = {
        ...formData,
        price: parseFloat(formData.price),
        original_price: formData.original_price ? parseFloat(formData.original_price) : null,
        start_date: formData.start_date || null,
        end_date: formData.end_date || null,
        requirements: formData.requirements ? formData.requirements.split('\n').map(s => s.trim()).filter(Boolean) : [],
        outcomes: formData.outcomes ? formData.outcomes.split('\n').map(s => s.trim()).filter(Boolean) : [],
        status: formData.is_active ? 'active' : 'draft',
      };
      delete payload.is_active;
      await api.post('/courses', payload);
      toast.success('Course posted successfully!');
      navigate('/courses');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Failed to post course'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-[#F0F4F8] py-8">
      <div className="max-w-4xl mx-auto px-4">
        <Button
          variant="ghost"
          onClick={() => navigate(-1)}
          className="mb-6 text-[#003366]"
        >
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back
        </Button>

        <Card>
          <CardHeader className="bg-gradient-to-r from-[#003366] to-[#004d99] text-white rounded-t-lg">
            <div className="flex items-center gap-3">
              <GraduationCap className="w-8 h-8" />
              <div>
                <CardTitle>Post a New Course</CardTitle>
                <CardDescription className="text-white/80">Share your expertise with students</CardDescription>
              </div>
            </div>
          </CardHeader>

          <CardContent className="pt-8">
            <form onSubmit={handleSubmit} className="space-y-8">
              {/* Basic Information */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="title">Course Title *</Label>
                    <Input
                      id="title"
                      name="title"
                      placeholder="e.g. Advanced React Development"
                      value={formData.title}
                      onChange={handleChange}
                      required
                    />
                  </div>

                  <div>
                    <Label htmlFor="description">Course Description *</Label>
                    <Textarea
                      id="description"
                      name="description"
                      placeholder="Describe what students will learn..."
                      value={formData.description}
                      onChange={handleChange}
                      rows={5}
                      required
                    />
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="category">Category *</Label>
                      <Select value={formData.category} onValueChange={(value) => setFormData({ ...formData, category: value })}>
                        <SelectTrigger id="category">
                          <SelectValue placeholder="Select category" />
                        </SelectTrigger>
                        <SelectContent>
                          {courseCategories.map(cat => (
                            <SelectItem key={cat.slug} value={cat.slug}>{cat.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    <div>
                      <Label htmlFor="subcategory">Subcategory</Label>
                      <Select
                        value={formData.subcategory}
                        onValueChange={(value) => setFormData({ ...formData, subcategory: value })}
                        disabled={courseSubcategories.length === 0}
                      >
                        <SelectTrigger id="subcategory">
                          <SelectValue placeholder={courseSubcategories.length === 0 ? 'No subcategories' : 'Select subcategory'} />
                        </SelectTrigger>
                        <SelectContent>
                          {courseSubcategories.map(sub => (
                            <SelectItem key={sub.id} value={sub.slug || sub.name}>{sub.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="level">Level *</Label>
                      <Select value={formData.level} onValueChange={(value) => setFormData({ ...formData, level: value })}>
                        <SelectTrigger id="level">
                          <SelectValue placeholder="Select level" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="beginner">Beginner</SelectItem>
                          <SelectItem value="intermediate">Intermediate</SelectItem>
                          <SelectItem value="advanced">Advanced</SelectItem>
                          <SelectItem value="apprenticeship">Apprenticeship</SelectItem>
                          <SelectItem value="award">Award</SelectItem>
                          <SelectItem value="certificate">Certificate</SelectItem>
                          <SelectItem value="diploma">Diploma</SelectItem>
                          <SelectItem value="higher_diploma">Higher Diploma</SelectItem>
                          <SelectItem value="graduate">Graduate</SelectItem>
                          <SelectItem value="postgraduate">Postgraduate</SelectItem>
                          <SelectItem value="all_levels">All Levels</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="institution_name">Institution / Provider Name *</Label>
                      <Input
                        id="institution_name"
                        name="institution_name"
                        placeholder="e.g. Your organisation's name"
                        value={formData.institution_name}
                        onChange={handleChange}
                        required
                      />
                    </div>
                    <div>
                      <Label htmlFor="instructor">Instructor / Trainer</Label>
                      <Input
                        id="instructor"
                        name="instructor"
                        placeholder="e.g. Jane Smith"
                        value={formData.instructor}
                        onChange={handleChange}
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Course Details */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Course Details</h3>
                <div className="space-y-4">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="duration">Duration *</Label>
                      <Input
                        id="duration"
                        name="duration"
                        placeholder="e.g. 6 weeks, 40 hours"
                        value={formData.duration}
                        onChange={handleChange}
                        required
                      />
                    </div>

                    <div>
                      <Label htmlFor="price">Price *</Label>
                      <div className="grid grid-cols-3 gap-2">
                        <Select value={formData.currency} onValueChange={(value) => setFormData({ ...formData, currency: value })}>
                          <SelectTrigger><SelectValue /></SelectTrigger>
                          <SelectContent>
                            {(currencies.length > 0 ? currencies : [{ code: 'GBP', symbol: '£', name: 'British Pound' }]).map(c => (
                              <SelectItem key={c.code} value={c.code}>{c.symbol} {c.code}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        <Input
                          id="price"
                          name="price"
                          type="number"
                          step="0.01"
                          placeholder="e.g. 99.99"
                          value={formData.price}
                          onChange={handleChange}
                          required
                          className="col-span-2"
                        />
                      </div>
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="original_price">Discount from price (optional)</Label>
                      <Input
                        id="original_price"
                        name="original_price"
                        type="number"
                        step="0.01"
                        placeholder="e.g. 149.99 (shown crossed out)"
                        value={formData.original_price}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="certification">Certification</Label>
                      <Input
                        id="certification"
                        name="certification"
                        placeholder="e.g. CPD-accredited certificate"
                        value={formData.certification}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-3 gap-4">
                    <div>
                      <Label htmlFor="delivery_mode">Delivery Mode</Label>
                      <Select value={formData.delivery_mode} onValueChange={(value) => setFormData({ ...formData, delivery_mode: value })}>
                        <SelectTrigger id="delivery_mode">
                          <SelectValue placeholder="Select mode" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="online">Online</SelectItem>
                          <SelectItem value="in_person">In Person</SelectItem>
                          <SelectItem value="hybrid">Hybrid</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="location">Location</Label>
                      <Input
                        id="location"
                        name="location"
                        placeholder="e.g. London, UK"
                        value={formData.location}
                        onChange={handleChange}
                        disabled={formData.delivery_mode === 'online'}
                      />
                    </div>
                    <div>
                      <Label htmlFor="video_url">Intro Video URL</Label>
                      <Input
                        id="video_url"
                        name="video_url"
                        placeholder="https://..."
                        value={formData.video_url}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="start_date">Start Date</Label>
                      <Input
                        id="start_date"
                        name="start_date"
                        type="date"
                        value={formData.start_date}
                        onChange={handleChange}
                      />
                    </div>
                    <div>
                      <Label htmlFor="end_date">End Date</Label>
                      <Input
                        id="end_date"
                        name="end_date"
                        type="date"
                        value={formData.end_date}
                        onChange={handleChange}
                      />
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="image_url">Course Image URL</Label>
                    <Input
                      id="image_url"
                      name="image_url"
                      placeholder="https://..."
                      value={formData.image_url}
                      onChange={handleChange}
                    />
                  </div>

                  <div>
                    <Label htmlFor="requirements">Requirements (one per line)</Label>
                    <Textarea
                      id="requirements"
                      name="requirements"
                      placeholder={'e.g.\nBasic computer literacy\nNo prior experience needed'}
                      value={formData.requirements}
                      onChange={handleChange}
                      rows={3}
                    />
                  </div>

                  <div>
                    <Label htmlFor="outcomes">Skills Gained / Outcomes (one per line)</Label>
                    <Textarea
                      id="outcomes"
                      name="outcomes"
                      placeholder={'e.g.\nBuild a professional CV\nAce your interview'}
                      value={formData.outcomes}
                      onChange={handleChange}
                      rows={3}
                    />
                  </div>
                </div>
              </div>

              {/* Status */}
              <div>
                <h3 className="text-lg font-semibold mb-4">Publish Status</h3>
                <div className="flex items-center gap-3 p-4 border rounded-lg bg-gray-50">
                  <Switch
                    id="is_active"
                    name="is_active"
                    checked={formData.is_active}
                    onCheckedChange={(checked) => setFormData({ ...formData, is_active: checked })}
                  />
                  <Label htmlFor="is_active" className="cursor-pointer">
                    {formData.is_active ? 'Course is active and visible' : 'Course is inactive (draft)'}
                  </Label>
                </div>
              </div>

              {/* Submit */}
              <div className="flex gap-3 pt-6 border-t">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => navigate(-1)}
                >
                  Cancel
                </Button>
                <Button
                  type="submit"
                  disabled={loading}
                  className="bg-emerald-600 hover:bg-emerald-700"
                >
                  {loading ? (
                    <>
                      <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                      Posting Course...
                    </>
                  ) : (
                    <>
                      <Plus className="w-4 h-4 mr-2" />
                      Post Course
                    </>
                  )}
                </Button>
              </div>
            </form>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
